import React, {
  ReactNode,
  FC,
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { DateTime } from 'luxon';
import { jwtDecode } from 'jwt-decode';
import { useAuth0 } from '@auth0/auth0-react';
import { useInterval } from 'usehooks-ts';
import { isLocalHostname } from '@shared/helpers/environment.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import * as amplitude from '@amplitude/analytics-browser';
import { useOrganizations, useSetOrganizations } from './store/organization';
import { useMatch } from 'react-router-dom';
import { useUserStore } from './store/user/user.store';
import { GlobalLoader } from '@ui/GlobalLoader';
import {
  createTokenRefreshInFlightLock,
  getTokenHydrationRetryIntervalMs,
  isRetryableTokenHydrationError,
  shouldFetchUserForTokenRefresh,
} from './helpers/auth0-token-refresh.helpers';

type State = {
  timer?: NodeJS.Timeout;
  expiry?: DateTime;
  shouldRetryHydration: boolean;
};

type Action =
  | { type: 'set expiry'; expiry?: DateTime }
  | { type: 'set timer'; timer?: NodeJS.Timeout }
  | { type: 'retry hydration' }
  | { type: 'stop' };

const reducer = (state: State, action: Action): State => {
  if (state.timer) {
    clearTimeout(state.timer);
  }

  switch (action.type) {
    case 'set expiry': {
      return {
        ...state,
        expiry: action.expiry,
        timer: undefined,
        shouldRetryHydration: false,
      };
    }
    case 'set timer': {
      return { ...state, timer: action.timer };
    }
    case 'retry hydration': {
      return { ...state, timer: undefined, shouldRetryHydration: true };
    }
    case 'stop': {
      return { ...state, timer: undefined };
    }
    default:
      return assertUnreachable(action);
  }
};

type Auth0TokenRefresherProps = {
  children: ReactNode;
};

const Auth0TokenRefresher: FC<Auth0TokenRefresherProps> = ({ children }) => {
  const {
    user: auth0User,
    isLoading: auth0isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const [state, dispatch] = useReducer(reducer, {
    shouldRetryHydration: false,
  });
  const fetchUser = useUserStore.getState().fetchUser;
  const refreshLockRef = useRef(createTokenRefreshInFlightLock());

  const urlRouteOrg = useMatch('/:org/projects/:id')?.params.org;
  const organizations = useOrganizations();
  const setOrganizations = useSetOrganizations();

  const refreshToken = useCallback(
    (ignoreCache?: boolean) => {
      if (!refreshLockRef.current.tryStart()) return;

      getAccessTokenSilently({
        cacheMode: ignoreCache ? 'off' : 'on',
      })
        .then(async (token) => {
          // auth0-react 2.25+ can resolve without a token; retry instead of decoding it.
          if (!token) {
            dispatch({ type: 'retry hydration' });
            return;
          }

          const decoded = jwtDecode(token);
          if (!decoded.exp) {
            dispatch({ type: 'retry hydration' });
            return;
          }

          try {
            const userId = auth0User?.sub;
            if (shouldFetchUserForTokenRefresh(userId, organizations.length)) {
              const user = await fetchUser(userId);
              setOrganizations(user.organizations, urlRouteOrg);
            }

            if (!isLocalHostname(window.location.hostname)) {
              amplitude.setUserId(auth0User?.email || String(decoded.sub));

              if (auth0User?.email) {
                const identifyEvent = new amplitude.Identify();
                const domain = auth0User.email.split('@')[1];

                identifyEvent.set('Email', auth0User.email);
                domain && identifyEvent.set('Domain', domain);

                amplitude.identify(identifyEvent);
              }
            }
          } catch (error: unknown) {
            if (isRetryableTokenHydrationError(error)) {
              dispatch({ type: 'retry hydration' });
              return;
            }
            console.error(error);
          }

          // Token is valid; leave the loader even when user fetch is a permanent failure.
          dispatch({
            type: 'set expiry',
            expiry: DateTime.fromSeconds(decoded.exp),
          });
        })
        .catch((error: unknown) => {
          if (isRetryableTokenHydrationError(error)) {
            dispatch({ type: 'retry hydration' });
            return;
          }
          console.error(error);
        })
        .finally(() => {
          refreshLockRef.current.end();
        });
    },
    [
      getAccessTokenSilently,
      auth0User?.sub,
      auth0User?.email,
      organizations.length,
      fetchUser,
      setOrganizations,
      urlRouteOrg,
    ],
  );

  useEffect(() => {
    if (!auth0isLoading) {
      refreshToken(false);
    }
  }, [getAccessTokenSilently, refreshToken, auth0isLoading, organizations]);

  useInterval(
    () => {
      refreshToken(false);
    },
    getTokenHydrationRetryIntervalMs({
      auth0IsLoading: auth0isLoading,
      hasExpiry: guards.is.defined(state.expiry),
      shouldRetryHydration: state.shouldRetryHydration,
    }),
  );

  useEffect(() => {
    const timeout =
      (state.expiry?.diffNow('milliseconds').milliseconds || 0) - 59000;
    if (timeout > 0 && !state.timer) {
      const timer = setTimeout(() => {
        refreshToken(true);
      }, timeout);
      dispatch({ type: 'set timer', timer: timer });
    }
  }, [refreshToken, state.expiry, state.timer]);

  useEffect(
    () => () => {
      // Stop the timer when component unmounts
      dispatch({ type: 'stop' });
    },
    [],
  );
  return !state.expiry ? (
    <GlobalLoader.Register text="Signing in" />
  ) : (
    <>{children}</>
  );
};

export default Auth0TokenRefresher;
